// Owner-инструменты закрытой беты: модалка инструментов и панель
// готовности. Вынесено из CampaignSetupPage.tsx без изменения
// поведения (Фаза 1).

import { useState } from "react";
import { api, type CampaignSetup, type BetaAdminDiagnostics, type BetaFactBackfill } from "../api";
import { BlockHeader, ParagraphText, type PageAction } from "./shared";

function OwnerBetaToolsModal({
  campaignId,
  setup,
  action,
  onClose,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: PageAction;
  onClose: () => void;
}) {
  return (
    <div className="cc-modal-backdrop cc-owner-beta-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-owner-beta-modal">
        <header className="cc-modal-header cc-owner-beta-modal-header">
          <div>
            <span>Настройки владельца</span>
            <h2>Тестирование беты</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>
        <ClosedBetaReadinessPanel
          campaignId={campaignId}
          setup={setup}
          action={action}
        />
      </div>
    </div>
  );
}
function ClosedBetaReadinessPanel({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: PageAction;
}) {
  const [diagnostics, setDiagnostics] = useState<BetaAdminDiagnostics | null>(
    null,
  );
  const [factBackfill, setFactBackfill] = useState<BetaFactBackfill | null>(
    null,
  );
  const [exportMessage, setExportMessage] = useState<string>("");
  const [exportPreview, setExportPreview] = useState<string>("");
  const readiness = setup.betaReadiness;
  async function refreshDiagnostics() {
    await action("Проверяем готовность закрытой беты…", async () => {
      const result = await api.betaDiagnostics(campaignId);
      setDiagnostics(result);
    });
  }
  async function backfillFacts() {
    await action("Создаём реестр фактов текущего цикла…", async () => {
      const result = await api.betaBackfillCurrentCycleFacts(campaignId);
      setFactBackfill(result);
      const refreshed = await api.betaDiagnostics(campaignId);
      setDiagnostics(refreshed);
    });
  }
  async function downloadDebugExport() {
    await action("Готовим debug export кампании…", async () => {
      const cycleNumber = diagnostics?.currentCycle?.cycleNumber;
      setExportPreview("");
      try {
        const link = await api.betaDebugExportLink(campaignId, cycleNumber);
        const anchor = document.createElement("a");
        anchor.href = link.downloadUrl;
        anchor.download = link.fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setExportMessage(
          `Открыта прямая ссылка на debug export без Telegram-заголовков. Ссылка действует до ${new Date(link.expiresAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}. Если Telegram не скачал файл автоматически, откройте ссылку во внешнем браузере.`,
        );
      } catch (directError) {
        const blob = await api.betaDebugExport(campaignId, cycleNumber);
        const text = await blob.text();
        setExportPreview(text);
        setExportMessage(
          `Прямая ссылка не настроена: ${directError instanceof Error ? directError.message : "неизвестная ошибка"}. JSON загружен ниже в поле просмотра — сохраните его вручную в файл city-chronicle-debug.json.`,
        );
      }
    });
  }
  return (
    <div className="cc-tab-stack cc-beta-readiness">
      <section
        className={`cc-block cc-beta-summary status-${readiness.status.toLowerCase()}`}
      >
        <BlockHeader
          eyebrow="Закрытая бета"
          title="Готовность кампании"
          icon="check"
        />
        <div className="cc-beta-score">
          <strong>{readiness.score}</strong>
          <span>{readiness.statusLabel}</span>
        </div>
        <ParagraphText text={readiness.summary} />
        <div className="cc-beta-metrics">
          <span>Блокеры: {readiness.blockers}</span>
          <span>Риски: {readiness.warnings}</span>
        </div>
        <div className="cc-beta-actions">
          <button
            type="button"
            className="secondary"
            onClick={() => void refreshDiagnostics()}
          >
            Обновить диагностику организатора
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void backfillFacts()}
          >
            Создать факты текущего цикла
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void downloadDebugExport()}
          >
            Скачать debug export
          </button>
        </div>
        {factBackfill && (
          <div className="cc-beta-fact-backfill">
            <strong>Реестр фактов</strong>
            <p>{factBackfill.summary}</p>
            <small>
              Было: {factBackfill.factsBefore} · создано:{" "}
              {factBackfill.factsCreated} · стало: {factBackfill.factsAfter}
            </small>
          </div>
        )}
        {exportMessage && (
          <div className="cc-beta-fact-backfill">
            <strong>Debug export</strong>
            <p>{exportMessage}</p>
            <small>
              Экспорт содержит приватные сведения кампании, не отправляйте его
              игрокам целиком.
            </small>
          </div>
        )}
        {exportPreview && (
          <div className="cc-beta-export-preview">
            <label htmlFor="debug-export-preview">
              <strong>JSON для ручного сохранения</strong>
            </label>
            <textarea
              id="debug-export-preview"
              readOnly
              value={exportPreview}
            />
            <small>
              Выделите содержимое поля, сохраните в файл .json и пришлите
              архивом вместе с backend-логом.
            </small>
          </div>
        )}
      </section>
      <section className="cc-block">
        <BlockHeader
          eyebrow="Чек-лист"
          title="Что проверять перед тестом"
          icon="activity"
        />
        <div className="cc-beta-section-list">
          {readiness.sections.map((section) => (
            <article
              className={`cc-beta-section status-${section.status.toLowerCase()}`}
              key={section.code}
            >
              <header>
                <strong>{section.title}</strong>
                <span>{section.statusLabel}</span>
              </header>
              <div>
                {section.checks.map((check) => (
                  <div
                    className={`cc-beta-check status-${check.status.toLowerCase()}`}
                    key={check.code}
                  >
                    <b>{check.label}</b>
                    <span>{check.statusLabel}</span>
                    <p>{check.detail}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="cc-block">
        <BlockHeader
          eyebrow="Правила тестирования"
          title="Принципы закрытой беты"
          icon="briefing"
        />
        <ul className="cc-beta-guidance">
          {readiness.testerGuidance.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
      {diagnostics && (
        <section className="cc-block cc-beta-admin-diagnostics">
          <BlockHeader
            eyebrow="Диагностика организатора"
            title="Текущее техническое состояние"
            icon="settings"
          />
          <p className="cc-explainer">{diagnostics.summary}</p>
          {diagnostics.currentCycle && (
            <div className="cc-beta-cycle-diagnostic">
              <span>Цикл №{diagnostics.currentCycle.cycleNumber}</span>
              <span>Фаза: {diagnostics.currentCycle.phase}</span>
              <span>Действий: {diagnostics.currentCycle.actionsTotal}</span>
              <span>
                Ожидают AI:{" "}
                {diagnostics.currentCycle.actionsWaitingForNarrative}
              </span>
              <span>NPC открыто: {diagnostics.currentCycle.npcVisitsOpen}</span>
              <span>Факты: {diagnostics.currentCycle.cycleFactsTotal}</span>
            </div>
          )}
          <div className="cc-beta-section-list">
            {diagnostics.checks.map((check) => (
              <article
                className={`cc-beta-check status-${check.status.toLowerCase()}`}
                key={check.code}
              >
                <b>{check.label}</b>
                <span>{check.statusLabel}</span>
                <p>{check.detail}</p>
              </article>
            ))}
          </div>
          <h3>Безопасные действия</h3>
          <ul className="cc-beta-guidance">
            {diagnostics.safeActions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export {
  OwnerBetaToolsModal,
  ClosedBetaReadinessPanel,
};
